import { useEffect, useRef } from 'react';
import { addMonths, getCalendarDays, isSameDay, isSameMonth, isToday, toDateString, WEEKDAY_LABELS } from '@/lib/dates';
import { getHoliday } from '@/lib/holidays';
import type { CalendarEvent } from '@/types';
import styles from './CalendarGrid.module.css';

const EVENT_COLOR_MAP: Record<string, string> = {
  blue:   'var(--event-blue)',
  purple: 'var(--event-purple)',
  green:  'var(--event-green)',
  orange: 'var(--event-orange)',
  pink:   'var(--event-pink)',
  cyan:   'var(--event-cyan)',
};

const MONTHS_BEFORE = 6;
const MONTHS_TOTAL  = 25;

interface Props {
  currentDate: Date;
  selectedDate: Date | null;
  events: CalendarEvent[];
  onSelectDate: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
}

export function CalendarGrid({ currentDate, selectedDate, events, onSelectDate, onMonthChange }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastScrolledKey = useRef('');
  const scrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // allMonths 는 마운트 시 한 번만 계산
  const baseDateRef = useRef<Date | null>(null);
  const allMonthsRef = useRef<Date[]>([]);
  if (!baseDateRef.current) {
    const d = new Date();
    d.setDate(1); d.setHours(0, 0, 0, 0);
    baseDateRef.current = addMonths(d, -MONTHS_BEFORE);
    allMonthsRef.current = Array.from(
      { length: MONTHS_TOTAL },
      (_, i) => addMonths(baseDateRef.current!, i),
    );
  }

  function monthKey(d: Date) { return `${d.getFullYear()}-${d.getMonth()}`; }
  function pageW() { return scrollRef.current?.offsetWidth ?? 0; }

  function scrollToMonth(date: Date, behavior: ScrollBehavior) {
    if (!scrollRef.current) return;
    const base = baseDateRef.current!;
    const diff = (date.getFullYear() - base.getFullYear()) * 12 + (date.getMonth() - base.getMonth());
    scrollRef.current.scrollTo({ left: Math.max(0, diff) * pageW(), behavior });
  }

  // 마운트: 현재 월로 즉시 이동
  useEffect(() => {
    scrollToMonth(currentDate, 'instant');
    lastScrolledKey.current = monthKey(currentDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // currentDate 외부 변경 → smooth 스크롤
  useEffect(() => {
    const key = monthKey(currentDate);
    if (key === lastScrolledKey.current) return;
    lastScrolledKey.current = key;
    scrollToMonth(currentDate, 'smooth');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  function handleScroll() {
    if (scrollTimer.current) clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      if (!scrollRef.current) return;
      const idx = Math.round(scrollRef.current.scrollLeft / pageW());
      const clamped = Math.max(0, Math.min(idx, allMonthsRef.current.length - 1));
      const month = allMonthsRef.current[clamped];
      if (month) {
        lastScrolledKey.current = monthKey(month);
        onMonthChange?.(month);
      }
    }, 80);
  }

  const eventsByDate = events.reduce<Record<string, CalendarEvent[]>>((acc, e) => {
    (acc[e.date] ??= []).push(e);
    return acc;
  }, {});

  return (
    <div className={styles.outer}>
      {/* 요일 헤더 고정 */}
      <div className={styles.weekdays}>
        {WEEKDAY_LABELS.map((label, i) => (
          <div key={label} className={`${styles.weekdayLabel} ${i === 0 ? styles.sunday : ''} ${i === 6 ? styles.saturday : ''}`}>
            {label}
          </div>
        ))}
      </div>

      {/* 가로 스크롤 (월 단위) */}
      <div ref={scrollRef} className={styles.scrollContainer} onScroll={handleScroll}>
        {allMonthsRef.current.map((monthDate, mi) => {
          const days = getCalendarDays(monthDate);
          const numRows = days.length / 7;

          return (
            <div key={mi} className={styles.monthPage}>
              <div className={styles.grid} style={{ '--grid-rows': numRows } as React.CSSProperties}>
                {days.map((day, idx) => {
                  const dateStr  = toDateString(day);
                  const dayEvts  = (eventsByDate[dateStr] || []).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
                  const inMonth  = isSameMonth(day, monthDate);
                  const today    = isToday(day);
                  const selected = selectedDate ? isSameDay(day, selectedDate) : false;
                  const dow      = day.getDay();
                  const holiday  = getHoliday(dateStr);
                  const visible  = dayEvts.slice(0, 3);
                  const overflow = dayEvts.length - 3;
                  const isRed    = holiday?.isRed || dow === 0;

                  return (
                    <button
                      key={idx}
                      className={[styles.dayCell, !inMonth ? styles.outside : '', today ? styles.today : '', selected ? styles.selected : ''].filter(Boolean).join(' ')}
                      onClick={() => onSelectDate(day)}
                      aria-label={dateStr}
                    >
                      <span className={[styles.dayNumber, isRed && inMonth ? styles.redText : '', dow === 6 && inMonth ? styles.saturdayText : '', !inMonth ? styles.outsideText : ''].filter(Boolean).join(' ')}>
                        {day.getDate()}
                      </span>
                      {holiday && inMonth && (
                        <span className={`${styles.holidayName} ${holiday.isRed ? styles.holidayRed : ''}`}>
                          {holiday.name}
                        </span>
                      )}
                      {visible.length > 0 && (
                        <div className={styles.chips}>
                          {visible.map(e => (
                            <span key={e.id} className={styles.chip} style={{
                              background: `${EVENT_COLOR_MAP[e.color] || EVENT_COLOR_MAP.blue}18`,
                              color: EVENT_COLOR_MAP[e.color] || EVENT_COLOR_MAP.blue,
                              borderLeft: `2px solid ${EVENT_COLOR_MAP[e.color] || EVENT_COLOR_MAP.blue}`,
                            }}>
                              {e.title}
                            </span>
                          ))}
                          {overflow > 0 && <span className={styles.overflow}>+{overflow}</span>}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
